/**
 * CAHPS decile-proximity insight (owner-approved 2026-07-20): per SSM, how far
 * the raw top-box rate is from the NEXT decile floor — surfaces "cheap points"
 * (compressed deciles where +1-2 points jumps a whole decile), "dead zones"
 * (rates below the entire benchmark distribution, pinned at the 1.0 minimum),
 * and top-decile SSMs.
 *
 * DISPLAY-ONLY arithmetic on the same benchmark rows the engine scores from —
 * no scoring behavior here. Caveat rendered wherever this shows: projections
 * run on RAW vendor rates; CMS scores case-mix-adjusted values, and with
 * decile widths this narrow a modest adjustment moves an SSM a full decile.
 */
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { parseDecileRange, type DecileBenchmark } from '@/lib/scoring-core';

export interface NextDecileInsight {
  kind: 'below_d1' | 'next' | 'top';
  /** The decile the delta reaches (1 for below_d1; undefined for top). */
  targetDecile?: number;
  /** Top-box points needed to reach the target decile's floor (≥0, 1dp). */
  delta?: number;
  /** The target decile's floor (%), for tooltips. */
  targetFloor?: number;
}

/** CAHPS SSMs are non-inverse (higher top-box = better). */
export function nextDecileInsight(ratePct: number, row: DecileBenchmark): NextDecileInsight | null {
  const floors: Array<{ decile: number; floor: number }> = [];
  for (let i = 1; i <= 10; i++) {
    const { min } = parseDecileRange(row[`decile_${i}` as keyof DecileBenchmark]);
    if (min != null) floors.push({ decile: i, floor: min });
  }
  if (floors.length === 0) return null;

  // Highest decile whose floor the rate has reached (0 = below the whole distribution).
  let current = 0;
  for (const f of floors) {
    if (ratePct >= f.floor) current = f.decile;
  }

  if (current === 0) {
    const d1 = floors[0];
    return { kind: 'below_d1', targetDecile: d1.decile,
      delta: Math.max(0, Math.round((d1.floor - ratePct) * 10) / 10), targetFloor: d1.floor };
  }
  const next = floors.find(f => f.decile > current);
  if (!next) return { kind: 'top' };
  return { kind: 'next', targetDecile: next.decile,
    delta: Math.max(0, Math.round((next.floor - ratePct) * 10) / 10), targetFloor: next.floor };
}

/** Shared caveat line (advisor-verified wording, 2026-07-20). */
export const CAHPS_PROJECTION_CAVEAT =
  'Projections use raw vendor top-box rates; CMS scores case-mix-adjusted values. ' +
  'With decile widths this narrow, a modest adjustment can move an SSM a full decile either way.';

/**
 * Plain-text rendering (owner 2026-07-20: the pill/arrow chrome read as
 * AI-generated) — quiet tabular text like the rest of the table, one
 * consistent format, color only where it carries meaning.
 */
export function NextDecileChip({ ratePct, row }: { ratePct: number; row: DecileBenchmark }) {
  const ins = nextDecileInsight(ratePct, row);
  if (!ins) return <span className="text-muted-foreground">—</span>;

  let text: string;
  let cls: string;
  let tip: string;
  if (ins.kind === 'top') {
    text = 'Top decile';
    cls = 'text-muted-foreground';
    tip = 'Already in decile 10 — no higher decile to reach.';
  } else if (ins.kind === 'below_d1') {
    text = `+${ins.delta!.toFixed(1)} to D1`;
    cls = 'text-amber-700 font-medium';
    tip = `Below the entire benchmark distribution — pinned at the 1.0-point minimum. No additional credit until the rate clears the decile-1 floor (${ins.targetFloor!.toFixed(2)}%).`;
  } else if ((ins.delta ?? 99) <= 2) {
    text = `+${ins.delta!.toFixed(1)} to D${ins.targetDecile}`;
    cls = 'text-emerald-700 font-medium';
    tip = `Within reach: +${ins.delta!.toFixed(1)} top-box points reaches decile ${ins.targetDecile} (floor ${ins.targetFloor!.toFixed(2)}%). Deciles are narrow here — small survey-experience gains move whole deciles.`;
  } else {
    text = `+${ins.delta!.toFixed(1)} to D${ins.targetDecile}`;
    cls = 'text-muted-foreground';
    tip = `Decile ${ins.targetDecile} starts at ${ins.targetFloor!.toFixed(2)}% — ${ins.delta!.toFixed(1)} top-box points away.`;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`cursor-default whitespace-nowrap tabular-nums text-sm ${cls}`}>{text}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
